import { useCallback, useEffect, useMemo } from "react"
import { t } from "@lingui/macro"
import { useNavigate } from "react-router-dom"
import { observer } from "mobx-react"

import { KnowledgeBaseHomeStore } from "src/views/knowledge-base/pages/home/store"
import { CardsContainer } from "src/views/knowledge-base/pages/home/styled"

import { GlobalStore } from "src/store"
import { useStore } from "src/store/lib/useStore"
import { IPageFilterProps } from "src/components/PageFilter"
import { SegmentPickerButton } from "src/components/SegmentPickerButton"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { ListPage } from "src/components/ListPage"
import { useInitializer } from "src/lib/initializer"
import { KnowledgeBaseItemCard } from "src/views/knowledge-base/pages/home/components/Card"
import { EKnowledgeBaseCardSection } from "src/views/knowledge-base/pages/home/types"
import { KnowledgeItemModal } from "src/modals/knowledge-items"
import { DEFAULT_ACCESS_GROUP } from "src/config"

const getCardTitle = (cardType: EKnowledgeBaseCardSection): string => {
    switch (cardType) {
        case EKnowledgeBaseCardSection.Documents:
            return t`knowledge-base.card.knowledge-card`
        case EKnowledgeBaseCardSection.NoticeBoard:
            return t`navigation.notice-board`
        case EKnowledgeBaseCardSection.Library:
            return t`global.features.library`
        default:
            return ""
    }
}

const View = observer(() => {
    const globalStore = useStore(GlobalStore)
    const store = useStore(KnowledgeBaseHomeStore)
    const navigate = useNavigate()

    const initialized = useInitializer(async () => {
        await store.init(
            globalStore.session.accessGroup.id ?? DEFAULT_ACCESS_GROUP.id,
        )
    }, [globalStore.session.accessGroup])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            store.dispose()
        }
    }, [store])

    const handleNewFeatureClick = useCallback(() => {
        globalStore.modals.open(() => <KnowledgeItemModal />)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const header = useMemo(
        () => ({
            header: t`knowledge-base.header`,
            breadcrumbs: [],
            createOptions: {
                item: t`knowledge-base.create-new`,
                onClick: handleNewFeatureClick,
            },
        }),
        [handleNewFeatureClick],
    )

    const handleSegmentChange = useCallback(
        (segments: number[]) => store.loadSegments(segments),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const filter = useMemo(
        (): IPageFilterProps => ({
            actions: (
                <SegmentPickerButton
                    value={store.segments}
                    onChange={handleSegmentChange}
                />
            ),
        }),
        [store.segments, handleSegmentChange],
    )

    const handleCardClick = useCallback(
        (cardType: EKnowledgeBaseCardSection) => {
            switch (cardType) {
                case EKnowledgeBaseCardSection.Documents:
                    navigate("list", {
                        state: {
                            knowledgeBaseCard: "Documents",
                        },
                    })
                    break
                case EKnowledgeBaseCardSection.NoticeBoard:
                    navigate("/notice-board")
                    break
                case EKnowledgeBaseCardSection.Library:
                    navigate("list", {
                        state: {
                            knowledgeBaseCard: "Library",
                            knowledgeBaseItemType: "information_item",
                        },
                    })
                    break
            }
        },
        [navigate],
    )

    return (
        <ListPage header={header} filter={filter} loading={!initialized}>
            <CardsContainer>
                {store.cards.map((card) => (
                    <KnowledgeBaseItemCard
                        key={card.id}
                        cardType={card.cardType}
                        title={getCardTitle(card.cardType)}
                        totalItems={card.totalItems}
                        onClick={() => handleCardClick(card.cardType)}
                    />
                ))}
            </CardsContainer>
        </ListPage>
    )
})

export const KnowledgeBaseHomeView = () => (
    <StoreProvider Store={KnowledgeBaseHomeStore}>
        <View />
    </StoreProvider>
)
